import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isLoadingSelector,
  loggedInUserSelector,
  userSelector,
} from "@/features/profile/store/userSlice";
import ProfileBrandLocationsBase from "@/components/ProfileBrandLocations";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import NoDataFound from "@/components/NoDataFound";
import { CheckCurrentPassword } from "@/features/auth";
import { getBrandsThunk } from "@/shared/store/brandSlice";
import { getUserThunk } from "@/features/profile/store/userSlice";
import PageLayout from "@/components/layouts/page/PageLayout";
import RpMUIDialog from "@/components/modals/RpMUIModal";
import ProfileCardBase from "./ProfileCard";
import ProfileEditBase from "./ProfileEdit";
import PageHeader from "@/components/layouts/pageHeaders/PageHeader";
import { USER_ROLE } from "@/config/constants";
import ChannelList from "./ChannelList";
import BrandProfileTabs from "./brand-profile/BrandProfileTabs";
import BrandProfileCardBase from "./brand-profile/BrandProfileCard";
import { Card } from "@mui/material";
import { brandService } from "@/shared/services/brandService";

const BrandProfileCard = BrandProfileCardBase as React.ComponentType<any>;
const ProfileEdit = ProfileEditBase as React.ComponentType<any>;
const ProfileCard = ProfileCardBase as React.ComponentType<any>;
const ProfileBrandLocations =
  ProfileBrandLocationsBase as React.ComponentType<any>;

const Profile = () => {
  const dispatch = useDispatch<any>();
  const loggedInUser = useSelector(loggedInUserSelector);
  const user = useSelector(userSelector);
  const isLoading = useSelector(isLoadingSelector);
  const loader = useContext(LoaderContext);
  const { loggedInUserRole, selectedBrand, userBrands } =
    useContext(GlobalContext);

  const { email } = user || {};

  // update password states
  const [checkCurrentPass, setCheckCurrentPass] = useState(false);
  const [isCurrentPassValid, setIsCurrentPassValid] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [brandForLocation, setBrandForLocation] = useState(null);
  const [brandProfileDetails, setBrandProfileDetails] = useState(null);
  const [edit, setEdit] = useState(false);

  /**
   * @method handleChangePassModalClose
   * @description handle on close modal
   */
  const handleChangePassModalClose = () => {
    setCheckCurrentPass(false);
    setIsCurrentPassValid(false);
  };

  const handleEditProfileModalClose = () => {
    setShowEditProfileModal(false);
  };
  const getBrandProfileDetails = () => {
    let isMounted = true;
    if (selectedBrand?.id) {
      loader(true);
      brandService
        .getBrandProfileDetails({ fk_brand_id: selectedBrand?.id })
        .then((res) => {
          if (isMounted) {
            const data = res.data;
            setBrandProfileDetails(data || {});
          }
        })
        .catch(() => {
          if (isMounted) {
            setBrandProfileDetails(null);
          }
        })
        .finally(() => {
          if (isMounted) loader(false);
        });
    }
    return () => {
      isMounted = false;
    };
  };

  useEffect(() => {
    let getBrandsRequestData: Record<string, any> = {
      fk_participant_id: 0,
      fk_company_id: 0,
    };

    if (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME) {
      getBrandsRequestData.fk_participant_id = loggedInUser?.participantData
        ? loggedInUser?.participantData?.id
        : 0;
    } else if (loggedInUserRole === USER_ROLE.PARENT_COMPANY.NAME) {
      getBrandsRequestData.fk_company_id = loggedInUser?.companyData
        ? loggedInUser?.companyData?.id
        : 0;
    } else if (
      loggedInUserRole === USER_ROLE.PARENT_COMPANY_USER.NAME ||
      loggedInUserRole === USER_ROLE.PARENT_COMPANY_SUB_USER.NAME
    ) {
      getBrandsRequestData.fk_company_id = loggedInUser?.companyData
        ? loggedInUser?.companyData?.fk_parent_company_id
        : 0;
    } else {
      getBrandsRequestData.id = selectedBrand?.id;
    }

    dispatch(getBrandsThunk(getBrandsRequestData));
    dispatch(getUserThunk({ ...loggedInUser, brandId: selectedBrand?.id }));
    getBrandProfileDetails();
    // Preserve legacy selected-brand fetch timing during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id]);

  useEffect(() => {
    loader(isLoading);
  }, [isLoading, loader]);

  //fetch the brand for location only when the user is PR
  useEffect(() => {
    if (userBrands && userBrands?.length) {
      if (loggedInUserRole === USER_ROLE.PARTICIPANT.NAME) {
        const b = userBrands.find((b) => b.id === selectedBrand?.id);
        handleOnClickBrand(b);
      }
      if (loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME) {
        return;
      }
    }
    // Preserve legacy brand-list synchronization timing during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userBrands, loggedInUserRole, selectedBrand]);

  const handleOnClickBrand = useCallback((brand) => {
    setBrandForLocation(brand);
  }, []);

  if (isLoading && !email) {
    return <NoDataFound message="Loading..." />;
  }

  return (
    <PageLayout className="profile-container mx-0 mx-sm-2">
      <PageHeader showName />

      <div className="profile participant-profile mt-4">
        <div className="row mb-4">
          {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
            <div className="col-lg-12">
              <BrandProfileCard
                loggedInUser={loggedInUser}
                loggedInUserRole={loggedInUserRole}
                user={user}
                setCheckCurrentPass={setCheckCurrentPass}
                setEdit={setEdit}
                brand={brandForLocation}
                selectedBrand={selectedBrand}
                setShowEditProfileModal={setShowEditProfileModal}
                loader={loader}
              />
            </div>
          )}
          <div
            className={`${loggedInUserRole === USER_ROLE.PARTICIPANT.NAME ? "col-xl-4 col-lg-4" : "col-lg-12"}   mb-3 mb-lg-0`}
          >
            {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
              <>
                {edit ? (
                  <ProfileEdit
                    loggedInUser={loggedInUser}
                    loggedInUserRole={loggedInUserRole}
                    user={user}
                    setCheckCurrentPass={setCheckCurrentPass}
                    edit={edit}
                    setEdit={setEdit}
                    loader={loader}
                    brand={brandForLocation}
                    selectedBrand={selectedBrand}
                  />
                ) : (
                  <ProfileCard
                    loggedInUser={loggedInUser}
                    loggedInUserRole={loggedInUserRole}
                    user={user}
                    setCheckCurrentPass={setCheckCurrentPass}
                    setEdit={setEdit}
                    brand={brandForLocation}
                    selectedBrand={selectedBrand}
                  />
                )}
              </>
            )}
          </div>

          {/* {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
            <div className="col mb-2">
              <ProfileBrandsGrid
                onClickBrand={handleOnClickBrand}
                user={user}
              />
            </div>
          )} */}

          {loggedInUserRole !== USER_ROLE.PARTICIPANT.NAME && (
            <div className="col-xl-12 mt-2">
              <BrandProfileTabs
                selectedBrand={selectedBrand}
                loader={loader}
                brandProfileDetails={brandProfileDetails}
                getBrandProfileDetails={getBrandProfileDetails}
                user={user}
              />
            </div>
          )}

          {loggedInUserRole === USER_ROLE.PARTICIPANT.NAME && (
            <div className="col-xl-8 col-lg-8">
              <Card className="profile-card-border p-4">
                <ChannelList
                  title="Channels"
                  subtitle="List of advertising channels available for your locations"
                  showRequestAccess={false}
                  showToolTip={false}
                  isLoading={isLoading}
                  channels={
                    user &&
                    user?.channelData?.map((ch) => ({
                      id: ch.id.toString(),
                      name: ch.channel_name,
                      status: ch.status ? "active" : "inactive",
                      image: ch.id,
                      altText: ch.channel_name,
                      grayscale: !ch.status,
                      requested_status: ch.requested_status,
                    }))
                  }
                  getBrandProfileDetails={() =>
                    dispatch(
                      getUserThunk({
                        ...loggedInUser,
                        brandId: selectedBrand?.id,
                      })
                    )
                  }
                />
              </Card>
              <div className="mt-4">
                <ProfileBrandLocations
                  brand={brandForLocation}
                  loggedInUserRole={loggedInUserRole}
                  selectedBrand={selectedBrand}
                />
              </div>
            </div>
          )}
        </div>

        {checkCurrentPass && (
          <RpMUIDialog
            open={checkCurrentPass}
            onClose={handleChangePassModalClose}
            heading={
              isCurrentPassValid ? "Update Password" : "Verify current password"
            }
            maxWidth="sm"
            fullWidth
          >
            <CheckCurrentPassword
              onCancel={handleChangePassModalClose}
              onChecked={(v: boolean) => setIsCurrentPassValid(v)}
              isCurrentPassValid={isCurrentPassValid}
              onUpdatePass={handleChangePassModalClose}
            />
          </RpMUIDialog>
        )}
        {showEditProfileModal && (
          <RpMUIDialog
            open={showEditProfileModal}
            onClose={handleEditProfileModalClose}
            heading={"Edit Profile"}
            maxWidth="sm"
            fullWidth
          >
            <div
              style={{
                textAlign: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <ProfileEdit
                loggedInUser={loggedInUser}
                loggedInUserRole={loggedInUserRole}
                user={user}
                setCheckCurrentPass={setCheckCurrentPass}
                edit={edit}
                setEdit={setEdit}
                loader={loader}
                brand={brandForLocation}
                selectedBrand={selectedBrand}
                setShowEditProfileModal={setShowEditProfileModal}
                getBrandProfileDetails={getBrandProfileDetails}
              />
            </div>
          </RpMUIDialog>
        )}
      </div>
    </PageLayout>
  );
};

export default Profile;
